import {useCallback} from 'react'

import {useSuiContext} from '@s-ui/react-context'
import {CONTACT_ACTION_IDS} from '@adv-mt/mt-form-contact-seller'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {criteoTrackEvents, useCriteoTrack} from '@adv-mt/mt-tracking-use-criteo'

/**
 * The v2 ContactCard emits no analytics of its own (invariant I3): every action
 * hands its payload up and this hook decides what to track.
 */
const useLeadTracking = ({adId, leadData = {}, trackingAd} = {}) => {
  const {domain} = useSuiContext()
  const {track} = useAnalyticsTrack()
  const {track: criteoTrack} = useCriteoTrack()

  const config = domain.get('config')
  const {HIT_ORIGINS, LEAD_TYPES, LIST_TRIGGERED_EVENT, TRACKING_EVENT_NAMES: EVENTS} = config.get('tracking')

  const {offer_type: offerType, dealer_id: dealerId} = leadData

  const trackSendMessageClick = useCallback(
    () =>
      domain
        .get('get_advertisements_tracking_use_case')
        .execute({ads: [trackingAd ?? {id: adId}], adsTypeName: 'ad_view', context: 'detail'})
        .then(({products} = {}) =>
          track({
            event: EVENTS.SEND_MESSAGE_BUTTON_CLICKED,
            data: {
              hit_origin: HIT_ORIGINS.DETAIL_PAGE,
              products,
              ...(offerType && {offer_type: offerType}),
              ...(dealerId && {dealer_id: dealerId})
            }
          })
        )
        .catch(() => {}),
    [domain, track, trackingAd, adId, offerType, dealerId, HIT_ORIGINS, EVENTS]
  )

  const trackAlertCreated = useCallback(
    alertId =>
      track({
        event: EVENTS.ALERT_CREATED,
        data: {hit_origin: HIT_ORIGINS.AD_REPLIED_EMAIL_ALERT_CREATED, hit_id: alertId}
      }),
    [track, HIT_ORIGINS, EVENTS]
  )

  const trackLeadError = useCallback(
    hitInformation =>
      track({
        event: EVENTS.LEAD_ERROR,
        data: {hit_origin: HIT_ORIGINS.DETAIL_PAGE_CONTACT_DRAWER, hit_information: hitInformation}
      }),
    [track, HIT_ORIGINS, EVENTS]
  )

  const trackLead = useCallback(
    ({leadType, hitInformation3, hitInformation4}) =>
      track({
        event: LIST_TRIGGERED_EVENT,
        // `...leadData` last is deliberate, matching legacy's email path
        // (DetailFormContactSellerWrapper.js:230-234). Legacy's whatsapp path (:144-146)
        // spreads it FIRST and lets hit_information3 win — that inconsistency is legacy's
        // own, and reordering here to "fix" it would diverge a payload that is byte-identical
        // today, which I3 forbids.
        //
        // Inert only by reachability: TrackingLeadMapper emits lead_type (and
        // hit_information, never hit_information3) solely when `modelDetailLeadType` is
        // passed, and nothing under app/src passes it. Wiring that param is what would make
        // leadData silently override the leadType argument below.
        //
        // seller_type is not recalculated here: the domain mapper is the source of truth
        // for this field, and the page propagates it untouched, same as legacy.
        data: {
          lead_type: leadType,
          ...(hitInformation3 && {hit_information3: hitInformation3}),
          ...(hitInformation4 && {hit_information4: hitInformation4}),
          ...leadData
        }
      }),
    [track, leadData, LIST_TRIGGERED_EVENT]
  )

  /**
   * A separate event, not a field of `Lead Triggered` — the `:`-joined chips live
   * on `Lead Enriched` in legacy (ContactButtons/index.js:270). With no chips
   * there is no event at all, so no empty string reaches the payload either.
   */
  const trackLeadEnriched = useCallback(
    selectedIntentions => {
      if (!selectedIntentions?.length) return

      track({
        event: EVENTS.LEAD_ENRICHED,
        data: {hit_origin: HIT_ORIGINS.CHAT_LEAD, hit_information: selectedIntentions.join(':')}
      })
    },
    [track, HIT_ORIGINS, EVENTS]
  )

  const onContact = useCallback(
    ({actionId, buyerIntentions, isFinancingLead, messageContactId, id, phone} = {}) => {
      switch (actionId) {
        case CONTACT_ACTION_IDS.WHATSAPP:
          return trackLead({leadType: LEAD_TYPES.DETAIL_AD_REPLIED_WHATSAPP, hitInformation3: id})
        case undefined:
          trackLeadEnriched(buyerIntentions)

          // Parity with legacy (DetailFormContactSellerWrapper.js:237-240): fired
          // unconditionally on every successful email/message lead, next to
          // `Lead Triggered` - a remarketing/conversion signal, not a UI event, so
          // it belongs here with the rest of this hook's tracking, never inside
          // the v2 form itself (invariant I3).
          criteoTrack({event: criteoTrackEvents.TRACK_TRANSACTION, eventData: {adId}})

          return isFinancingLead
            ? trackLead({leadType: LEAD_TYPES.FINANCED_PURCHASE_EMAIL, hitInformation3: messageContactId})
            : trackLead({
                leadType: LEAD_TYPES.DETAIL_AD_REPLIED_EMAIL,
                hitInformation3: messageContactId,
                hitInformation4: phone
              })
        default:
          return undefined
      }
    },
    [trackLead, trackLeadEnriched, criteoTrack, adId, LEAD_TYPES]
  )

  // No enrichment here on purpose: legacy's `showLeadEnrichment` block lives inside
  // the email submit handler in ContactButtons, so a phone lead never enriched.
  return {onContact, trackAlertCreated, trackLeadError, trackSendMessageClick}
}

export default useLeadTracking
